/**
 * Generic Navigation Handlers
 * Hooks up to AEM provided nav component
 * Attaches event handlers for menus / sub-menus
 * @author yauheni.mukavozchyk@isobar.com
 */

import { hasAttributeInScope } from 'utils/dom';
import { navigationKeyEvent } from 'utils/accessibility/navigationKeyEvent';
import { setAriaDrawerTags } from 'utils/accessibility/aria';
import { scopeTab } from 'utils/accessibility/scopeTab';
import utils from 'utils';
import { ANALYTICS } from 'constants';
import { removeExpandedClass } from '../languageToggle';

let isPreviousSubMenuOpenId = null;
/**
 * Finds closest drawer (top menu item) for the domEL
 * In some cases click events would point to domEl deeply nested inside of the top menu item
 * @param {DOMElement} domEl
 */
const findClosestDrawerEl = (domEl) => {
  if (domEl.hasAttribute('aria-controls')) {
    return domEl;
  }
  const closestDrawer = domEl.parentElement.querySelector('[aria-controls]');

  return closestDrawer || findClosestDrawerEl(domEl.parentElement);
};

/**
 * Finds closest drawer (top menu item) and associated sub-menu
 * @param {DOMElement} domEl
 */
export const findMenuAndSubMenuEls = (domEl) => {
  const menuEl = findClosestDrawerEl(domEl);
  const subMenuId = menuEl.getAttribute('aria-controls');
  const subMenuEl = document.getElementById(subMenuId);
  return { menuEl, subMenuEl };
};

const getHeaderWrapper = () => document.querySelector('.headerWrapper');
const overlayClass = 'show-overlay';
const scrollLockClass = 'lock-scroll';

export const setPreviousAriaDrawerTags = (el = isPreviousSubMenuOpenId, isOpening = false, isExapnded = false) => {
  if (el) {
    const controls = document.querySelector(`[aria-controls="${el.id}"]`);
    el.setAttribute('aria-hidden', !isOpening);
    controls && controls.setAttribute('aria-expanded', isExapnded);
    return el;
  }
  return null;
};

/**
 * Click handler sub-menu controller
 * @param {event} e
 */
const onMenuControllerClick = (e) => {
  e.stopImmediatePropagation();
  e.preventDefault();

  const { menuEl, subMenuEl } = findMenuAndSubMenuEls(e.srcElement);
  let isMenuOpen = menuEl.getAttribute('aria-expanded') === 'true';
  const isSubMenuOpen = menuEl?.nextElementSibling?.getAttribute('aria-hidden') === 'true';

  // Removing expanded state of subMenu elements
  isPreviousSubMenuOpenId && setPreviousAriaDrawerTags(isPreviousSubMenuOpenId, false, false);
  isPreviousSubMenuOpenId = null;
  removeExpandedClass('navigation utility__toggle expanded');
  const buttonElements = subMenuEl.querySelectorAll('button');
  let expandededStatus = false;
  buttonElements?.forEach((button) => {
    if (button?.getAttribute('aria-expanded') === 'true') {
      button?.setAttribute('aria-expanded', false);
      expandededStatus = true;
    }
    return expandededStatus;
  });

  !expandededStatus && setAriaDrawerTags(subMenuEl, !isMenuOpen);

  !isMenuOpen && setAriaDrawerTags(subMenuEl, !isMenuOpen);
  if (menuEl.getAttribute('id') !== 'header-hamburger-btn') {
    isPreviousSubMenuOpenId = subMenuEl;
    localStorage.setItem('isPreviousSubMenuOpenId', isPreviousSubMenuOpenId.id);
  }

  utils.trapFocus('.header', 'li, button, #header-hamburger-btn', () => subMenuEl.offsetWidth === window.innerWidth);

  // if Sub menu open, toggle the overlay class on the headerWrapper
  const headerWrapper = getHeaderWrapper();

  window.innerWidth > 1279 && isSubMenuOpen
    ? headerWrapper.classList.add(overlayClass)
    : headerWrapper.classList.remove(overlayClass);

  utils.analytics.interaction('menu', 'nav', menuEl.innerText.toLowerCase());
  const isHamburgerMenu = window.innerWidth <= 1280;
  const allTabbableMenuItems = utils.accessibility.findTabbableDescendants(subMenuEl, false);
  allTabbableMenuItems[allTabbableMenuItems.indexOf(e.target) + 1].focus();

  // focus to the first child when side menu is open
  if (isHamburgerMenu) {
    allTabbableMenuItems[0].focus();
  }

  const isHamburgerButton = e?.target?.classList?.contains('header__mobile-menu-btn');

  if (isHamburgerButton && isMenuOpen) {
    headerWrapper?.offsetParent?.classList?.remove(scrollLockClass);
  } else if (isHamburgerMenu) {
    headerWrapper?.offsetParent?.classList?.add(scrollLockClass);
  }

  if (isHamburgerButton && isMenuOpen) {
    headerWrapper?.classList?.remove(overlayClass);
  } else if (isHamburgerMenu) {
    headerWrapper?.classList?.add(overlayClass);
  }

  const navigationWrapper = document.querySelector('#header-navigation');
  const hamburgerBtn = document.querySelector('#header-hamburger-btn');

  const handleOutsideClick = (event) => {
    if (!navigationWrapper?.contains(event.target) && !hamburgerBtn?.contains(event.target)) {
      if (navigationWrapper) {
        navigationWrapper.setAttribute('aria-hidden', 'true');
        hamburgerBtn.setAttribute('aria-expanded', 'false');
        headerWrapper?.classList?.remove(overlayClass);
        headerWrapper?.offsetParent?.classList?.remove(scrollLockClass);

        document.removeEventListener('click', handleOutsideClick);
        isMenuOpen = false;
      }
    }
  };

  if (!isMenuOpen) {
    document.addEventListener('click', handleOutsideClick);
  }
};

/**
 * Attach handlers for generic nav routine
 * @param {Object} params
 */
export default function init(params) {
  const { domEl, isVerticalNav = false, isSpaceClick = true } = params;
  // all tabbable elements in the nav including not visible ones
  const allTabbableMenuItems = utils.accessibility.findTabbableDescendants(domEl, false);
  const elem = document.getElementById('header-navigation');

  // set aria-current for active breadcumb
  const breadcrumbActive = document.querySelector('li.breadcrumb__active-item');
  breadcrumbActive?.setAttribute('aria-current', 'page');

  const isHamburgerMenu = window.innerWidth <= 1280;
  const headerWrapper = getHeaderWrapper();

  function checkAriaTags() {
    // Collapse menu by default on smaller resolutions
    if (window.innerWidth > 1279) {
      setAriaDrawerTags(elem, true);
    } else {
      headerWrapper?.offsetParent?.classList?.remove(scrollLockClass);
      headerWrapper?.classList?.remove(overlayClass);
      setAriaDrawerTags(elem, false);
    }
  }

  checkAriaTags();
  window.onresize = checkAriaTags;

  if (allTabbableMenuItems.length) {
    domEl.addEventListener('click', (e) => {
      if (hasAttributeInScope(e.target, domEl, 'aria-controls')) {
        return onMenuControllerClick(e);
      }
      return e;
    });

    domEl.addEventListener('keydown', (e) => {
      // If sidebar menu is open, scope tabbing inside header elements
      if (isHamburgerMenu && elem.getAttribute('aria-hidden') === 'false') {
        scopeTab(domEl, e);
      }
      if (
        elem.getAttribute('aria-hidden') === 'false' &&
        allTabbableMenuItems.indexOf(e.target) === allTabbableMenuItems.length - 1
      ) {
        setAriaDrawerTags(elem, false);
      }
      if (allTabbableMenuItems.indexOf(e.target) !== -1) {
        return navigationKeyEvent({
          isVertical: isVerticalNav || window.innerWidth < 1280,
          isSpaceClick,
          domEl,
          e,
        });
      }
      return e;
    });
  }
  document.addEventListener('click', (e) => {
    const element = document.querySelector('.navigation__container[aria-hidden=false]');

    if (element && !element.contains(e.target)) {
      setAriaDrawerTags(element, false);
      element.focus();

      // Sub menu is open and click outside, remove the overlay class on the headerWrapper
      headerWrapper.classList.contains(overlayClass) && headerWrapper.classList.remove(overlayClass);
    }

    // Adds analytics for 'chat with us' CTA
    if (e.target?.id === 'contact-us-chat') {
      utils.analytics.interaction(ANALYTICS.UI_BUTTON, ANALYTICS.CHAT_WITH_US, ANALYTICS.UI_LINK);
    }
  });
}
